import axiosLib, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { store } from "utils/store/configureStore";
import i18n from "utils/i18n";

import { signOutUser, setAlertMessage } from "utils/actions/index";
import { noteFailure, noteSuccess } from "utils/maintenance";
import { reportApiFailure } from "utils/datadog";

const host = process.env.REACT_APP_API_URL;
const baseHost = process.env.REACT_APP_BASE_API_URL;

type Params = Record<string, unknown>;

let axios: AxiosInstance | undefined;

// Set by the first 401/403 on an instance and cleared when the next one is
// built at sign-in. The app boots with several requests in flight, so one
// expired session used to dispatch one sign-out (and one alert) per request.
let signingOut = false;

// Creates a local shared instance of Axios if doesn't exist yet.
const checkAxiosInit = () => {
  if (!axios) {
    signingOut = false;
    axios = axiosLib.create({
      timeout: 60000,
      withCredentials: true,
      headers: {
        common: {
          Accept: "application/json",
          "content-type": "application/json"
        }
      }
    });

    axios.interceptors.response.use(
      response => {
        // Outage detection. This instance is what almost the whole app talks
        // to, so it has to report here as well as in utils/requestMethodsBase
        // — that older client covers only a handful of call sites, and
        // detection wired solely to it would barely see real traffic.
        noteSuccess();
        return response;
      },
      error => {
        // A cancelled request is not evidence of an outage — axios rejects
        // those the same way it rejects a real failure.
        if (!axiosLib.isCancel?.(error)) {
          // Only a 5xx or no answer is logged. The console.error this
          // replaced was picked up by Datadog for every status, so each
          // expired session showed up as a burst of errors.
          reportApiFailure(error);
          noteFailure(error.response?.status, error.config?.url);
        }
        return Promise.reject(error);
      }
    );
  }
};

export const signOutAxios = () => {
  axios = undefined;
};

export const handleError = (err: AxiosError) => {
  if (err.response) {
    const { status } = err.response;
    if ((status === 401 || status === 403) && !signingOut) {
      signingOut = true;
      store.dispatch(setAlertMessage(i18n.t("Api.Error.Authorization"), "error"));
      store.dispatch(signOutUser());
    }
  }
  return err;
};

export const sendGet = async (
  path: string,
  params?: Params,
  base = false,
  config: AxiosRequestConfig = {}
) => {
  const needsToBeBase = base ? baseHost : host;
  checkAxiosInit();
  return axios.get(needsToBeBase + path, { ...config, params }).catch(err => {
    return Promise.reject(handleError(err));
  });
};

export const sendGetWithoutSignOut = async (path: string, params?: Params) => {
  checkAxiosInit();
  return axios.get(host + path, { params }).catch(err => {
    return Promise.reject(err);
  });
};

export const sendPost = async (path: string, params?: unknown, base = false) => {
  const needsToBeBase = base ? baseHost : host;

  checkAxiosInit();
  return axios.post(needsToBeBase + path, params).catch(err => {
    return Promise.reject(handleError(err));
  });
};

export const sendPostWithoutSignOut = async (path: string, params?: unknown, config?: AxiosRequestConfig) => {
  checkAxiosInit();
  return axios.post(host + path, params, config).catch(err => {
    return Promise.reject(err);
  });
};

export const sendDeleteWithoutSignOut = async (path: string, params?: AxiosRequestConfig) => {
  checkAxiosInit();
  return axios.delete(host + path, params).catch(err => {
    return Promise.reject(err);
  });
};

export const sendPut = async (path: string, params?: unknown) => {
  checkAxiosInit();
  return axios.put(host + path, params).catch(err => {
    return Promise.reject(handleError(err));
  });
};

export const sendPutWithoutSignOut = async (path: string, params?: unknown) => {
  checkAxiosInit();
  return axios.put(host + path, params).catch(err => {
    return Promise.reject(err);
  });
};

export const sendPatch = async (path: string, params?: unknown) => {
  checkAxiosInit();
  return axios.patch(host + path, params);
};

export const sendDelete = async (path: string, params?: AxiosRequestConfig) => {
  checkAxiosInit();
  return axios.delete(host + path, params).catch(err => {
    return Promise.reject(handleError(err));
  });
};

const queryParams = (source: Params): string => {
  let array: string[] = [];

  for (let key in source) {
    array.push(encodeURIComponent(key) + "=" + encodeURIComponent(source[key] as string));
  }

  return array.join("&");
};

export const mountUrlWithParams = (url: string, params: Params | null = {}): string => {
  if (params && Object.keys(params).length) {
    const queryParamsSerialized = queryParams(params);
    // Keys and values are already encodeURIComponent-ed; encoding again turned % into %25.
    return `${url}?${queryParamsSerialized}`;
  } else {
    return `${url}`;
  }
};
